/**
 * ManufacturerTable — Step 2 rich detail.
 *
 * Candidate factories pulled from Phase-3-style sourcing data. Status
 * cycles queued → contacted → quoted → passed/awarded so the
 * stakeholder can see how the workflow drives day-to-day actions.
 */
import React from 'react';
import { CheckCircle2, Globe2, Mail, Building2 } from 'lucide-react';
import { useManifest } from '../ManifestContext';

const STATUS_LABELS = {
  queued:    'Queued',
  contacted: 'Contacted',
  quoted:    'Quoted',
  awarded:   'Selected',
  passed:    'Passed',
};

const cycleStatus = (s) => {
  const order = ['queued', 'contacted', 'quoted', 'awarded', 'passed'];
  const i = order.indexOf(s);
  return order[(i + 1) % order.length];
};

const ManufacturerTable = ({ order }) => {
  const { updateOrderFields } = useManifest();

  const updateFactoryStatus = (factoryId, newStatus) => {
    updateOrderFields({
      factories: order.factories.map((f) =>
        f.id === factoryId ? { ...f, status: newStatus } : f,
      ),
    });
  };

  const counts = order.factories.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="manifest-rich-panel manifest-factories">
      <div className="manifest-rich-panel-head">
        <div className="manifest-rich-panel-title">
          <Building2 size={16} /> Candidate manufacturers
        </div>
        <div className="manifest-factories-counts">
          {['queued', 'contacted', 'quoted', 'awarded', 'passed'].map((s) =>
            counts[s] ? (
              <span key={s} className={`manifest-factory-count status-${s}`}>
                {counts[s]} {STATUS_LABELS[s].toLowerCase()}
              </span>
            ) : null,
          )}
        </div>
      </div>

      <div className="manifest-factory-table-wrap">
        <table className="manifest-factory-table">
          <thead>
            <tr>
              <th>Factory</th>
              <th>Country</th>
              <th>Verified</th>
              <th>Years</th>
              <th>MOQ</th>
              <th>Lead time</th>
              <th>Listed price</th>
              <th>Contact</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {order.factories.map((f) => (
              <tr key={f.id} className={`status-${f.status}`}>
                <td className="cell-name">{f.name}</td>
                <td><Globe2 size={11} /> {f.country}</td>
                <td>
                  {f.verified ? <CheckCircle2 size={14} className="cell-verified" /> : <span className="cell-unverified">—</span>}
                </td>
                <td>{f.yearsActive}y</td>
                <td>{f.moq}</td>
                <td>{f.leadTimeDays}d</td>
                <td>${f.listedPrice}</td>
                <td className="cell-contact">
                  <a href={`mailto:${f.contact}`}><Mail size={11} /> {f.contact}</a>
                </td>
                <td>
                  <button
                    type="button"
                    className={`manifest-factory-status status-${f.status}`}
                    onClick={() => updateFactoryStatus(f.id, cycleStatus(f.status))}
                    title="Click to advance status"
                  >
                    {STATUS_LABELS[f.status]}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManufacturerTable;
